import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import {
  IGetApplicationSettings,
  IGetApplicationSettingsData,
  IUpdateApplicationSettings,
  IUpdateApplicationSettingsRequest,
} from '../../../shared/interfaces/application-settings.interface';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { TranslocoService } from '@ngneat/transloco';
import { NotificationService } from '../../../shared/services/notification.service';
import { Subject, takeUntil } from 'rxjs';
import { environment } from '../../../../environments/environment';

@Component({
  selector: 'app-application-settings-dialog',
  templateUrl: './application-settings-dialog.component.html',
  styleUrl: './application-settings-dialog.component.scss',
})
export class ApplicationSettingsDialogComponent implements OnInit, OnDestroy {
  private translocoService = inject(TranslocoService);
  private config = inject(DynamicDialogConfig);
  private notificationService = inject(NotificationService);
  private ref = inject(DynamicDialogRef);
  private fb = inject(FormBuilder);
  isLoad = false;
  applicationSettingsForm!: FormGroup;
  applicationSettings!: IGetApplicationSettings;
  private destroy$: Subject<void> = new Subject<void>();
  environment = environment;

  ngOnInit() {
    this.applicationSettings = this.config.data.model;

    this.applicationSettingsForm = this.fb.group({
      result: this.fb.array([]),
    });
    this.setApplicationSettings(this.applicationSettings.result);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  setApplicationSettings(
    settingsDataArray: IGetApplicationSettingsData[],
  ): void {
    const settingsFormArray = this.applicationSettingsForm.get(
      'result',
    ) as FormArray;
    settingsDataArray.forEach((settingData) => {
      settingsFormArray.push(this.createSettingFormGroup(settingData));
    });
    this.isLoad = true;
  }

  createSettingFormGroup(settingData: IGetApplicationSettingsData): FormGroup {
    return this.fb.group({
      id: [{ value: settingData.id, visible: false }],
      name: [{ value: settingData.name, disabled: true }, Validators.required],
      value: [settingData.value, Validators.required],
      valueDisabled: [
        { value: settingData.value, disabled: true },
        Validators.required,
      ],
    });
  }

  get settingsFormControls() {
    return (this.applicationSettingsForm.get('result') as FormArray).controls;
  }

  closeDialog(): void {
    const message = this.translocoService.translate(
      'applicationSettings.confirm.cancel.message',
    );
    const title = this.translocoService.translate(
      'applicationSettings.confirm.cancel.title',
    );

    this.notificationService
      .confirm(title, message)
      .onClose.pipe(takeUntil(this.destroy$))
      .subscribe((data: boolean) => {
        if (data) {
          this.ref.close(false);
        }
      });
  }

  saveApplicationSettings(): void {
    const message = this.translocoService.translate(
      'applicationSettings.labels.checkUpdate',
    );
    const title = this.translocoService.translate(
      'applicationSettings.labels.checkUpdateTitle',
    );

    this.notificationService
      .confirm(title, message)
      .onClose.pipe(takeUntil(this.destroy$))
      .subscribe((data: boolean) => {
        if (data) {
          const model = this.createUpdateApplicationSettings();
          this.ref.close(model);
        }
      });
  }

  createUpdateApplicationSettings(): IUpdateApplicationSettingsRequest {
    const model: IUpdateApplicationSettings[] = [];
    this.settingsFormControls.forEach((control) => {
      const updateApplicationSetting: IUpdateApplicationSettings = {
        id: control.get('id')!.value.value,
        value: control.get('value')!.value,
      };
      model.push(updateApplicationSetting);
    });

    const updateApplicationSettingsRequest: IUpdateApplicationSettingsRequest =
      {
        updateApplicationSettingsList: model,
      };

    return updateApplicationSettingsRequest;
  }
}
